import type { BasketItem, ProductCustomerType } from '@accesso/cc-ecomm-api';
import type { GTMItem, GTMProduct, GTMProductVariant } from '@accesso/cc-std-lib';
import type { BasketModel, ProductModel } from '@accesso/cc-ui/model';

import type { CartViewDetails } from './event-mapper.models';

export interface ProductImpression {
	/**
	 * The position in the list of products it was viewed in
	 */
	index: number;
	/**
	 * The product model
	 */
	product: ProductModel;
}

export type ProductModelToGtmProductOptions = Partial<{
	/**
	 * The view that the product is being displayed in such as "product-details", "category-page" or "cart-screen"
	 */
	view: string;
	/**
	 * If this product was viewed in a category list, the identifier of the category
	 */
	categoryId: string;
}>;

/**
 * Converts a list of ProductImpressionItems into a list of GTMProducts
 * @param impression The impression
 * @param options Any options
 */
export function productImpressionsToGtmProductList(impressions: ProductImpression[], options: ProductModelToGtmProductOptions = {}): GTMProduct[] {
	return impressions.map((impression): GTMProduct => productImpressionToGtmProduct(impression, options));
}

/**
 * Converts a ProductImpressionItem into a GTMProduct
 * @param impression The impression
 * @param options Any options
 */
export function productImpressionToGtmProduct(impression: ProductImpression, options: ProductModelToGtmProductOptions = {}): GTMProduct {
	const { index, product } = impression;
	return productModelToGtmModel(product, { ...options, index });
}

/**
 * Converts a product model to our GTM product model
 * @param product The product
 * @param options Options to add
 * @returns
 */
export function productModelToGtmModel(
	product: ProductModel,
	options: Omit<Partial<ProductImpression & ProductModelToGtmProductOptions>, 'product'> = {}
): GTMProduct {
	const { view = '', categoryId, index } = options;
	const { retail, tax, fee: fees, display } = product.price() ?? { retail: 0, tax: 0, fee: 0, display: 0 };

	return {
		id: product.id,
		externalId: product.externalId(),
		name: product.name(),
		isDated: product.dateRequired(),
		isTimed: product.timeRequired(),
		view,
		display,
		retail,
		tax,
		fees,
		discount: 0,
		index,
		categoryId,
		variant: product.customerTypes().map(customerTypeToProductVariant),
	};
}

export function customerTypeToProductVariant({ id, name, price }: ProductCustomerType): GTMProductVariant {
	const { retail, tax, fee, display, gate } = price ?? { retail: 0, tax: 0, fee: 0, display: 0 };
	return {
		id,
		name,
		retail,
		tax,
		fees: fee,
		display,
		gate,
		discount: 0,
	};
}

export function basketItemToGtmItem(item: BasketItem, index = 0, overrideQuantity = 0): GTMItem {
	const { price, productId: id, externalProductId: externalId, productName: name, customerTypeId, customerTypeName, quantity } = item;

	const { retail, tax, fees } = price ?? { retail: 0, tax: 0, fees: 0 };

	return <GTMItem>{
		id: id.toString(),
		externalId,
		customerTypeId: customerTypeId.toString(),
		customerTypeName,
		name,
		quantity: overrideQuantity > 0 ? overrideQuantity : quantity,
		retail,
		tax,
		fees,
		discount: 0,
		index,
	};
}

/**
 * Creates the details model for cart view events
 * @param basket The basket model
 */
export function getBasketEventDetails(currency: string, basket: BasketModel): CartViewDetails {
	const summary = basket.summary();

	return {
		currency,
		retail: summary.retail,
		tax: summary.tax,
		fees: summary.fees,
		// Discount not implemented yet
		discount: 0,
		total: summary.due,
		// Adjustments not implemented yet
		adjustments: {
			cartFee: 0,
			cartDiscount: 0,
		},
		items: basket.items().map((item, index) => basketItemToGtmItem(item, index)),
	};
}
